import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ServiceItemKind, ServiceType } from "@garage/shared";
import { api } from "./client";
import { vehicleKeys } from "./vehicles";

export interface ServiceItem {
  id?: string;
  kind: ServiceItemKind;
  name: string;
  partNo: string | null;
  quantity: string;
  unitCost: string | null;
  amount: string | null;
  warrantyMonths: number | null;
}

export interface Attachment {
  id: string;
  kind: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

export interface Service {
  id: string;
  vehicleId: string;
  type: ServiceType;
  title: string;
  serviceDate: string;
  durationDays: number;
  odometerKm: number | null;
  totalCost: string;
  labourCost: string | null;
  partsCost: string | null;
  centreName: string | null;
  centreCity: string | null;
  nextDueDate: string | null;
  nextDueKm: number | null;
  notes: string | null;
  items: ServiceItem[];
  attachments: Attachment[];
}

export interface ServiceRow {
  id: string;
  type: ServiceType;
  title: string;
  serviceDate: string;
  durationDays: number;
  odometerKm: number | null;
  totalCost: string;
  centreName: string | null;
  itemCount: number;
  attachmentCount: number;
}

export interface ServiceListResponse {
  items: ServiceRow[];
  page: { number: number; limit: number; total: number; pages: number };
  totals: { cost: string; count: number; avgCost: string };
  facets: {
    years: number[];
    types: { type: ServiceType; count: number }[];
  };
}

export type ServiceFilters = Record<string, string | undefined>;

export const serviceKeys = {
  all: ["services"] as const,
  list: (vehicleId: string, filters: ServiceFilters) =>
    ["services", vehicleId, filters] as const,
  one: (id: string) => ["services", "one", id] as const,
};

export function useServices(vehicleId: string, filters: ServiceFilters = {}) {
  const qs = new URLSearchParams(
    Object.entries(filters).filter(([, v]) => v) as [string, string][],
  ).toString();

  return useQuery({
    queryKey: serviceKeys.list(vehicleId, filters),
    queryFn: () =>
      api<ServiceListResponse>(
        `/api/vehicles/${vehicleId}/services${qs ? `?${qs}` : ""}`,
      ),
    enabled: Boolean(vehicleId),
  });
}

export function useService(id: string) {
  return useQuery({
    queryKey: serviceKeys.one(id),
    queryFn: () => api<{ service: Service }>(`/api/services/${id}`),
    enabled: Boolean(id),
  });
}

/** Both list and vehicle caches move when a service changes: the vehicle card
 *  shows lifetime spend and the odometer, and both are derived from services. */
function useServiceInvalidation() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: serviceKeys.all });
    qc.invalidateQueries({ queryKey: vehicleKeys.all });
  };
}

export function useSaveService(vehicleId: string, serviceId?: string) {
  const invalidate = useServiceInvalidation();
  return useMutation({
    mutationFn: (body: unknown) =>
      api<{ service: Service }>(
        serviceId
          ? `/api/services/${serviceId}`
          : `/api/vehicles/${vehicleId}/services`,
        { method: serviceId ? "PATCH" : "POST", body },
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteService() {
  const invalidate = useServiceInvalidation();
  return useMutation({
    mutationFn: (id: string) => api(`/api/services/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
}

export async function attachmentUrl(id: string): Promise<string> {
  const { url } = await api<{ url: string }>(`/api/attachments/${id}/url`);
  return url;
}

export function useDeleteAttachment() {
  const invalidate = useServiceInvalidation();
  return useMutation({
    mutationFn: (id: string) =>
      api(`/api/attachments/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
}
